'use client';

import { useState, useEffect } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Navigation, Pagination, Autoplay } from 'swiper/modules';
import FullscreenImageViewer from './FullscreenImageViewer';
import { OptimizedImage } from './OptimizedImage';
import { cacheImages } from '@/services/imageCacheService';

interface ImageCarouselProps {
  images: string[];
  alt?: string;
  className?: string;
  imageClassName?: string;
  showNavigation?: boolean;
  showPagination?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  navigationId?: string; // Unique ID for navigation buttons
  buttonSize?: 'sm' | 'md' | 'lg'; // Different button sizes
  slidesPerView?: number;
  spaceBetween?: number;
  breakpoints?: {
    [width: number]: {
      slidesPerView: number;
      spaceBetween?: number;
    };
  };
  enableFullscreen?: boolean; // Enable fullscreen viewer on click
  fullscreenTitle?: string; // Title for fullscreen viewer
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images = [],
  alt = 'Image',
  className = '',
  imageClassName = 'object-cover',
  showNavigation = true,
  showPagination = false,
  autoplay = false,
  loop = false,
  navigationId = 'default',
  buttonSize = 'md',
  slidesPerView = 1,
  spaceBetween = 10,
  breakpoints,
  enableFullscreen = false,
  fullscreenTitle = '',
}) => {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [clickedImageIndex, setClickedImageIndex] = useState(0);

  const imageCount = images.length;
  const validImages = images.filter(img => img && img.length > 0);

  // Determine object-fit based on imageClassName
  const getObjectFit = (): 'cover' | 'contain' => {
    if (imageClassName?.includes('object-contain')) {
      return 'contain';
    }
    return 'cover';
  };

  const objectFit = getObjectFit();

  // Preload images in background for faster carousel transitions
  useEffect(() => {
    if (validImages.length === 0) return;

    // Preload first 5 images immediately, rest will be loaded on demand
    const imagesToPreload = validImages.slice(0, 5);
    cacheImages(imagesToPreload).catch(() => {
      // Silently fail - caching is non-critical
    });
  }, [validImages]);

  const handleImageClick = (index: number) => {
    if (enableFullscreen && validImages.length > 0) {
      setClickedImageIndex(index);
      setIsFullscreenOpen(true);
    }
  };

  if (validImages.length === 0) {
    const noImageArray = ['/assets/images/No-Image.jpg'];
    return (
      <>
        <div
          className={`relative ${className} ${enableFullscreen ? 'cursor-pointer' : ''}`}
          onClick={() => enableFullscreen && handleImageClick(0)}
        >
          <OptimizedImage
            src='/assets/images/No-Image.jpg'
            alt='No image available'
            fill
            style={{ objectFit: objectFit }}
            className={imageClassName}
            priority
            loading='eager'
            sizes='(max-width: 768px) 100vw, 320px'
          />
        </div>

        {/* Fullscreen Image Viewer for no-image case */}
        {enableFullscreen && (
          <FullscreenImageViewer
            images={noImageArray}
            isOpen={isFullscreenOpen}
            onClose={() => setIsFullscreenOpen(false)}
            initialIndex={0}
            title={fullscreenTitle || alt}
            autoRotateInterval={3000}
          />
        )}
      </>
    );
  }

  if (validImages.length === 1) {
    return (
      <>
        <div
          className={`relative ${className} ${enableFullscreen ? 'cursor-pointer' : ''}`}
          onClick={() => handleImageClick(0)}
        >
          <OptimizedImage
            src={validImages[0]}
            alt={alt}
            fill
            style={{ objectFit: objectFit }}
            className={imageClassName}
            priority
            loading='eager'
            sizes='(max-width: 768px) 100vw, 320px'
            onError={() => {
              // Error handling is done in OptimizedImage
            }}
          />
        </div>

        {/* Fullscreen Image Viewer for single image */}
        {enableFullscreen && (
          <FullscreenImageViewer
            images={validImages}
            isOpen={isFullscreenOpen}
            onClose={() => setIsFullscreenOpen(false)}
            initialIndex={0}
            title={fullscreenTitle || alt}
            autoRotateInterval={3000}
          />
        )}
      </>
    );
  }

  const handleSlideChange = (swiper: SwiperType) => {
    // Only track states when not in loop mode
    if (!loop && swiper) {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    }
  };

  // Button sizing
  const buttonSizeClasses = {
    sm: { button: 'w-6 h-6', icon: '14' },
    md: { button: 'w-8 h-8', icon: '20' },
    lg: { button: 'w-10 h-10', icon: '24' },
  };

  const { button: buttonClass, icon: iconSize } =
    buttonSizeClasses[buttonSize] || buttonSizeClasses.md;

  const shouldShowNavButtons = showNavigation && imageCount > 1;

  return (
    <>
      <div className={`relative w-full ${className}`}>
        <Swiper
          slidesPerView={slidesPerView}
          spaceBetween={spaceBetween}
          loop={loop && validImages.length > 1}
          autoplay={
            autoplay && validImages.length > 1
              ? {
                  delay: 3000,
                  disableOnInteraction: false,
                }
              : false
          }
          pagination={
            showPagination && validImages.length > 1
              ? {
                  clickable: true,
                }
              : false
          }
          navigation={
            shouldShowNavButtons && validImages.length > 1
              ? {
                  prevEl: `#swiper-button-prev-${navigationId}`,
                  nextEl: `#swiper-button-next-${navigationId}`,
                }
              : false
          }
          modules={[Navigation, Pagination, A11y, Autoplay]}
          className='w-full h-full'
          style={
            {
              // Add smooth transitions to prevent sharp edges
              '--swiper-transition-timing-function':
                'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '--swiper-transition-duration': '300ms',
            } as React.CSSProperties
          }
          onSlideChange={handleSlideChange}
          onInit={handleSlideChange}
          breakpoints={breakpoints}
        >
          {validImages.map((image, index) => (
            <SwiperSlide key={`${image}-${index}`}>
              <div
                className={`relative w-full h-full ${enableFullscreen ? 'cursor-pointer' : ''}`}
                onClick={() => handleImageClick(index)}
              >
                <OptimizedImage
                  src={image}
                  alt={`${alt} ${index + 1}`}
                  fill
                  style={{ objectFit: objectFit }}
                  className={imageClassName}
                  priority={index === 0}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  sizes='(max-width: 768px) 100vw, 320px'
                  onError={() => {
                    // Error handling is done in OptimizedImage
                  }}
                />
              </div>
            </SwiperSlide>
          ))}

          {/* Navigation Buttons */}
          {shouldShowNavButtons && (
            <>
              <div
                id={`swiper-button-prev-${navigationId}`}
                className={`!flex items-center justify-center absolute z-10 top-1/2 left-1 -translate-y-1/2 ${buttonClass} bg-black/50 rounded-full transition-all duration-300 ${
                  !loop && isBeginning
                    ? 'opacity-0 pointer-events-none'
                    : 'opacity-100 cursor-pointer'
                }`}
              >
                <svg
                  width={iconSize}
                  height={iconSize}
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z'
                    fill='rgba(255, 255, 255, 0.9)'
                  />
                </svg>
              </div>
              <div
                id={`swiper-button-next-${navigationId}`}
                className={`!flex items-center justify-center absolute z-10 top-1/2 right-1 -translate-y-1/2 ${buttonClass} bg-black/50 rounded-full transition-all duration-300 ${
                  !loop && isEnd
                    ? 'opacity-0 pointer-events-none'
                    : 'opacity-100 cursor-pointer'
                }`}
              >
                <svg
                  width={iconSize}
                  height={iconSize}
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'
                    fill='rgba(255, 255, 255, 0.9)'
                  />
                </svg>
              </div>
            </>
          )}
        </Swiper>
      </div>

      {/* Fullscreen Image Viewer */}
      {enableFullscreen && (
        <FullscreenImageViewer
          images={validImages}
          isOpen={isFullscreenOpen}
          onClose={() => setIsFullscreenOpen(false)}
          initialIndex={clickedImageIndex}
          title={fullscreenTitle || alt}
          autoRotateInterval={3000}
        />
      )}
    </>
  );
};

export default ImageCarousel;
